import { hasValue, isIE } from './utils';

/**
 * Check if a targeted DOM element has a specific class name
 *
 * @param {Element} element targeted DOM element
 * @param {String} className  css class name to check
 * @return {Boolean}
 */
export function hasClass(element, className) {
    if (!element) {
        return false;
    }

    if (element.classList) {
        return element.classList.contains(className);
    }
    return element.className.split(' ').indexOf(className) > -1;
}

/**
 * Remove a specific class name to a DOM element
 *
 * @param {Element} element targeted DOM element
 * @param {String} className css class name to remove
 */
export function removeClass(element, className) {
    if (!element) {
        return;
    }

    if (element.classList) {
        element.classList.remove(className);
    } else {
        const classList = element.className.split(' ');
        let finalClassList = '';
        classList.splice(classList.indexOf(className), 1);
        classList.forEach(item => {
            finalClassList += ` ${item}`;
        });

        // eslint-disable-next-line no-param-reassign
        element.className = finalClassList.trim();
    }
}

/**
 * Add a specific class name to a DOM element
 *
 * @param {Element} element targeted DOM element
 * @param {String} className css class name to add
 */
export function addClass(element, className) {
    if (!element) {
        return;
    }

    if (element.classList) {
        element.classList.add(className);
    } else {
        // eslint-disable-next-line no-param-reassign
        element.className = `${element.className} ${className}`.trim();
    }
}

/**
 * Toggle a specific class name to a DOM element with possibility to force
 *
 * @param {Element} element targeted DOM element
 * @param {String} className css class name to toggle
 * @param {Boolean*} force determine wether the class should be added (true) or removed (false)
 */
export function toggleClass(element, className, force) {
    if (!element) {
        return;
    }

    if (element.classList && !isIE()) {
        element.classList.toggle(className, force);
    } else if (hasValue(force)) {
        if (force) {
            addClass(element, className);
        } else {
            removeClass(element, className);
        }
    } else if (hasClass(element, className)) {
        removeClass(element, className);
    } else {
        addClass(element, className);
    }
}

/**
 * Toggle a specific attribute on a DOM element with possibility to force
 *
 * @param {Element} element targeted DOM element
 * @param {String} attributeName attribute name to toggle
 * @param {Boolean*} force determine wether the class should be added (true) or removed (false)
 */
export function toggleAttribute(element, attributeName, force) {
    if (!element) {
        return;
    }

    if (hasValue(force)) {
        if (force) {
            element.setAttribute(attributeName, true);
        } else {
            element.removeAttribute(attributeName);
        }
    } else if (element.hasAttribute(attributeName)) {
        element.removeAttribute(attributeName);
    } else {
        element.setAttribute(attributeName, true);
    }
}

/**
 * Get all the parents of a given element. Like jQuery.parents() does
 * @param {Element} element targeted DOM element
 * @param {String} limitSelector Stop the recursion of parents if the selector matches with the current parent
 * @return {Array}
 */
export function getParents(element, limitSelector = null) {
    const result = [];
    for (let p = element && element.parentElement; p; p = p.parentElement) {
        result.push(p);
        if (limitSelector && p.matches(limitSelector)) {
            return result;
        }
    }
    return result;
}

/**
 * Get the parent of a given element. If limitSelector is defined, find recursively a parent for the given selector
 * @param {Element} element targeted DOM element
 * @param {String} limitSelector Stop the recursion of parents if the selector matches with the current parent
 * @return {Element}
 */
export function getParent(element, limitSelector = null) {
    if (!element) {
        return null;
    }

    if (limitSelector === null) {
        return element.parentElement;
    }
    for (let p = element && element.parentElement; p; p = p.parentElement) {
        if (limitSelector && p.matches(limitSelector)) {
            return p;
        }
    }
    return null;
}

/**
 * Browse all the parents and check if one has the given selector
 * @param {Element} element targeted DOM element
 * @param {String} selector Stop the recursion of parents if the selector matches with the current parent
 * @return {Boolean} Return true if one is found
 */
export function hasParent(element, selector = null) {
    for (let p = element && element.parentElement; p; p = p.parentElement) {
        if (selector && p.matches(selector)) {
            return true;
        }
    }
    return false;
}

/**
 * Dispatch a custom event
 *
 * @param {String} name The name of the event
 * @param {Object} detail The data associated with the event
 * @returns {Event|CustomEvent} The event
 */
export function dispatchEvent(name, detail, element = document) {
    let event;

    const nameStr = String(name);

    try {
        event = detail === undefined ? new Event(nameStr) : new CustomEvent(nameStr, { detail });
    } catch (e) {
        if (detail === undefined) {
            event = element.createEvent('Event');
            event.initEvent(nameStr, true, true);
        } else {
            event = element.createEvent('CustomEvent');
            event.initCustomEvent(nameStr, true, true, detail);
        }
    }

    element.dispatchEvent(event);
    return event;
}

/**
 *  Create a Node Element from a html string
 *
 * @param {String} htmlString
 * @returns {Node} the node element created
 */
export function createElementFromHtmlString(htmlString) {
    const div = document.createElement('div');
    div.innerHTML = htmlString.trim();

    // Change this to div.childNodes to support multiple top-level nodes
    return div.firstChild;
}
